import {Component, NgZone} from '@angular/core';
import {AlertController, Events, IonicPage, LoadingController, NavController, Platform} from 'ionic-angular';
import {AssociationConfiguration} from '../../models/association.config';
import {VizixService} from '../../providers/vizix.service';
import {StatusBar} from '@ionic-native/status-bar';
import {TranslateService} from '@ngx-translate/core';
import {isUndefined} from 'ionic-angular/util/util';
import {PatchThingRequestItem} from '../../models/PatchThingRequestItem';
import {Keyboard} from '@ionic-native/keyboard';
import {NativeAudio} from '@ionic-native/native-audio';
import {VizixEventService} from '../../providers/vizix.event.service';
import {
  ITEM_EVENT_ASSOCIATION_TAG,
  ITEM_EVENT_RE_ASSOCIATION_TAG, ITEM_UDF_ACTIVITY, ITEM_UDF_CATEGORY, ITEM_UDF_SOURCE, ITEM_UDF_SUB_CATEGORY,
  ITEM_UDF_TS_REASSOCIATED,
  ITEM_UDF_TS_REASSOCIATED_DATE,
  ITEM_UDF_USER,
  ITEM_UDF_UUID_MOBILE, RETURN_PAGE_MANAGE_TAGS,
  VIZIX_EVENT_ID_CREATE_ASSOCIATION,
  VIZIX_EVENT_ID_CREATE_DEFAULT_PRODUCT,
  VIZIX_EVENT_ID_UPDATE_ASSOCIATION,
  VIZIX_THING_ITEM_UDF_SOURCE,
  VizixEventActivity,
  VizixEventCategory,
  VizixEventEventType,
  VizixEventSubCategory
} from '../../app/app.constants';
import {InsomniaService} from '../../providers/insomnia.service';
import {ManageTagsBase} from '../../models/ManageTagsBase';


export interface AudioConfig {
  id: string;
  assetPath: string;
  volume: number;
  voices: number;
  delay: number;
  resolve: (value: any) => void;
}

@IonicPage()
@Component({
  selector: 'page-manage-tags',
  templateUrl: 'manage-tags.html',
})

export class ManageTagsPage extends ManageTagsBase {

  public quantitySaved: number;
  public quantityUpdated: number;
  public productSerialNumber: string;

  /**
   * Manage Tag Association Page View
   *
   * @param {NavController} navCtrl
   * @param {VizixService} vizixService
   * @param {VizixEventService} vizixEventService
   * @param {StatusBar} statusBar
   * @param {AlertController} alertCtrl
   * @param {Platform} platform
   * @param {LoadingController} loadingCtrl
   * @param {Keyboard} keyBoard
   * @param {TranslateService} translate
   * @param {NgZone} ngZone
   * @param {Events} events
   * @param {NativeAudio} nativeAudio
   * @param {InsomniaService} insomniaService
   */
  constructor(public navCtrl: NavController,
              public vizixService: VizixService,
              public vizixEventService: VizixEventService,
              public statusBar: StatusBar,
              public alertCtrl: AlertController,
              public platform: Platform,
              public loadingCtrl: LoadingController,
              public keyBoard: Keyboard,
              public translate: TranslateService,
              public ngZone: NgZone,
              public events: Events,
              public nativeAudio: NativeAudio,
              public insomniaService: InsomniaService) {
    super(navCtrl, vizixService, vizixEventService, statusBar, alertCtrl, platform, loadingCtrl, keyBoard, translate, ngZone, events, nativeAudio, insomniaService);
    this.initializeComplementaryVariables();
  }

  /**
   * Initialize class property values
   */
  initializeComplementaryVariables() {
    this.returnPage = RETURN_PAGE_MANAGE_TAGS;
    this.tag = '[ASSOCIATE_TAGS]';
    this.config = this.vizixService.configurationApp.AssociationConfiguration as AssociationConfiguration;
    this.TAG_TYPE = this.config.FirstFieldLabel.value;
    this.quantitySaved = 0;
    this.quantityUpdated = 0;
    this.productSerialNumber = '';
  }

  /**
   * Start the association process
   * @param {any} params
   */
  public onAssociateTag(params: { config: AssociationConfiguration, firstField: string, secondField: string }) {

    // Update values from component
    this.config = params.config;
    this.firstField = params.firstField;
    this.secondField = params.secondField;

    if(this.firstField && this.firstField.length && this.secondField && this.secondField.length){
      this.onLoading({
        content: this.translate.instant('manage_tags.loading_association'),
        dismissOnPageChange: true
      })
        .then(() => {
          this.vizixService.VizixGetThingAssociation(this.config.FirstThingTypeCode.value, this.config.FirstBarcodeUDF.value, this.firstField, false)
            .subscribe(
              (data: Array<any>) => {
                if (data.length > 0) // UPC (first field)  exists in Products
                {
                  if (!this.getProductUDFValue(this.config.DefaultProductUPCUDF.value, data[0])) {
                    this.dismissLoading();
                    this.onAlert({
                      type: 'error',
                      title: this.translate.instant('manage_tags.dialog_error_locate_product_title'),
                      message: this.translate.instant('manage_tags.dialog_error_udf_product_message'),
                      okButtonText: this.translate.instant('manage_tags.buttons.ok')
                    });
                    this.resetForm();
                  } else {
                    this.verifySecondFieldExist();
                  }
                }
                else {
                  this.dismissLoading();
                  if (this.config.AllowDefaultProductInsert.value) {
                    this.onAlert({
                      type: 'alert',
                      title: this.translate.instant('manage_tags.dialog_default_product_created_title'),
                      message: this.translate.instant('manage_tags.dialog_default_product_created_message'),
                      okButtonText: this.translate.instant('manage_tags.buttons.ok'),
                      callback: () => this.insertDefaultProduct()
                    })

                  }
                  else {
                    this.onAlert({
                      type: 'error',
                      title: this.translate.instant('manage_tags.dialog_error_locate_product_title'),
                      message: this.translate.instant('manage_tags.dialog_error_locate_product_message'),
                      okButtonText: this.translate.instant('manage_tags.buttons.ok')
                    });
                    this.resetForm();
                  }
                }
              },
              error => {
                this.dismissLoading();
                this.onAlert({
                  type: 'error',
                  title: this.translate.instant('manage_tags.dialog_error_title'),
                  message: this.translate.instant('manage_tags.dialog_error_message'),
                  okButtonText: this.translate.instant('manage_tags.buttons.ok')
                });
                this.resetForm();
              });
        });
    }
  }

  /**
   * Get Item UPC Value
   * @param itemFromVizix
   * @returns {string}
   */
  private getItemUPCValue(itemFromVizix: any): string {
    if (itemFromVizix[0]) {
      let response = itemFromVizix[0];
      if (response[this.config.ItemUPCUDF.value]) {
        return response[this.config.ItemUPCUDF.value].value;
      }
    }
    return '';
  }

  /**
   * Get Product UDF Value
   * @param {string} UDFName
   * @param productFromVizix
   * @returns {boolean}
   */
  private getProductUDFValue(UDFName: string, productFromVizix: any): boolean {
    if (productFromVizix[UDFName]) {
      this.productSerialNumber = productFromVizix[UDFName];
    } else {
      this.productSerialNumber = '';
      return false;
    }
    return true;
  }

  /**
   * Generate and save vizix event
   *
   * @param {VizixEventActivity} activity
   * @param {string} eventId
   * @param {string} thingTypeCode
   * @param {string} firstFieldText
   * @param {string} secondFieldText
   * @param {boolean} isError
   * @param {string} errorMessage
   */
  private generateAndSaveVizixEvent(activity: VizixEventActivity,
                                    eventId: string,
                                    thingTypeCode: string,
                                    firstFieldText?: string,
                                    secondFieldText?: string,
                                    isError?: boolean,
                                    errorMessage?: string) {
    this.vizixEventService.getVizixEventItemBase(isError).then((vizixEvent) => {
      vizixEvent.category = VizixEventCategory.WORKFLOW;
      vizixEvent.subCategory = VizixEventSubCategory.MANAGE_TAGS;
      vizixEvent.eventType = VizixEventEventType.INFO;
      vizixEvent.activity = activity;

      if (isError) {
        vizixEvent.detail = this.vizixEventService.generateDetailsMessage(errorMessage,
          thingTypeCode, this.getGroupByThingTypeCode(thingTypeCode), this.vizixService.configurationApp.AppParametersConfiguration.DefaultDepartment.value, this.selectedZone.name);
      } else {
        if (firstFieldText && secondFieldText) {
          let detailsMessage = {
            from: firstFieldText,
            to: secondFieldText
          };
          vizixEvent.detail = this.vizixEventService.generateDetailsMessage(detailsMessage,
            thingTypeCode, this.getGroupByThingTypeCode(thingTypeCode), this.vizixService.configurationApp.AppParametersConfiguration.DefaultDepartment.value, this.selectedZone.name);
        } else if (firstFieldText) {
          let detailsMessage = {
            created: firstFieldText
          };
          vizixEvent.detail = this.vizixEventService.generateDetailsMessage(detailsMessage,
            thingTypeCode, this.getGroupByThingTypeCode(thingTypeCode), this.vizixService.configurationApp.AppParametersConfiguration.DefaultDepartment.value, this.selectedZone.name);
        }
      }

      vizixEvent.count = 1;
      vizixEvent.eventId = eventId;
      this.vizixEventService.recordEvent(vizixEvent);
    }, (error) => {
      console.log(this.tag, 'Error getting VizixItemBase', error);
    });
  }

  /**
   * Get group by thing type code
   * @param {string} thingTypeCode
   * @returns {string}
   */
  private getGroupByThingTypeCode(thingTypeCode: string): string {
    if (thingTypeCode == this.config.ThingTypeCodeToSync.value) {
      return this.thingTypeCodeToSyncGroup;
    } else if (thingTypeCode == this.config.FirstThingTypeCode.value) {
      return this.firstThingTypeCodeGroup;
    } else {
      return '';
    }
  }

  /**
   * Verify second field existence
   */
  private verifySecondFieldExist() {
    this.vizixService.VizixGetThingAssociation(
      this.config.SecondThingTypeCode.value,
      this.config.SecondBarcodeUDF.value,
      this.secondField,
      true)
      .subscribe(
        (data: any) => {
          if (data.length > 0) // EPC (second field) exist in Items
          {
            this.dismissLoading();
            if (this.config.AllowAssociationOverride.value) {
              let messageText;

              if (this.getItemUPCValue(data) == '' || isUndefined(this.getItemUPCValue(data))) {
                messageText = <string> this.translate.instant('manage_tags.dialog_override_message_unassociated');
                messageText = messageText.replace('${secondFieldLabel}', this.config.SecondFieldLabel.value);
                messageText = messageText.replace('${rfid}', this.secondField);
              }
              else {
                messageText = <string> this.translate.instant('manage_tags.dialog_override_message');
                messageText = messageText.replace('${secondFieldLabel}', this.config.SecondFieldLabel.value);
                messageText = messageText.replace('${firstFieldLabel}', this.config.FirstFieldLabel.value);
                messageText = messageText.replace('${rfid}', this.secondField);
                messageText = messageText.replace('${upc}', this.getItemUPCValue(data));
              }

              this.onAlert({
                type: 'cancel',
                title: this.translate.instant('manage_tags.dialog_override_title'), message: messageText,
                okButtonText: this.translate.instant('manage_tags.buttons.ok'),
                callback: () => this.updateAssociation(),
                cancelButtonText: this.translate.instant('manage_tags.buttons.cancel'),
              });
            }
            else {
              this.onAlert({
                type: 'error',
                title: this.translate.instant('manage_tags.dialog_error_override_forbidden_title'),
                message: this.translate.instant('manage_tags.dialog_error_override_forbidden_message'),
                okButtonText: this.translate.instant('manage_tags.buttons.ok')
              });
              this.resetForm();
            }
          }
          else {
            this.createAssociation();
          }
        },
        error => {
          this.dismissLoading();
          this.onAlert({
            type: 'error',
            title: this.translate.instant('manage_tags.dialog_error_title'),
            message: this.translate.instant('manage_tags.dialog_error_message'),
            okButtonText: this.translate.instant('manage_tags.buttons.ok')
          });
          console.log(this.tag, error);
          this.resetForm();
        });
  }

  /**
   * Create new tag association
   */
  private createAssociation() {

    let body: PatchThingRequestItem[] = [];
    let requestItem = new PatchThingRequestItem();
    requestItem.timestamp = Date.now();
    requestItem.serialNumber = this.secondField;
    requestItem.group = this.vizixService.configurationApp.AppParametersConfiguration.Group.value;
    requestItem.name = this.secondField;
    requestItem.thingTypeCode = this.config.ThingTypeCodeToSync.value;

    requestItem.udfs = {};
    requestItem.udfs[this.config.AssociationZoneUDF.value] = {value: String(this.vizixService.getStoreId()) + '_' + this.selectedZone.zoneTypeCode};
    requestItem.udfs[this.vizixService.configurationApp.AppParametersConfiguration.ItemEventUDF.value] = {value: ITEM_EVENT_ASSOCIATION_TAG};
    // TODO: MOVE TO DATA ENTRY

    requestItem.udfs[ITEM_UDF_TS_REASSOCIATED] = {value: true};
    requestItem.udfs[ITEM_UDF_TS_REASSOCIATED_DATE] = {value: this.vizixService.vizixTime()};
    requestItem.udfs[ITEM_UDF_USER] = {value: this.username};
    requestItem.udfs[ITEM_UDF_UUID_MOBILE] = {value: this.uuid};
    requestItem.udfs[ITEM_UDF_SOURCE] = {value: VIZIX_THING_ITEM_UDF_SOURCE};
    requestItem.udfs[ITEM_UDF_CATEGORY] = {value: 'Workflow'};
    requestItem.udfs[ITEM_UDF_SUB_CATEGORY] = {value: 'Manage Tags'};
    requestItem.udfs[ITEM_UDF_ACTIVITY] = {value: 'Create Association'};
    requestItem.udfs[this.config.ItemUPCUDF.value] = {value: this.productSerialNumber};

    body.push(requestItem);
    // this.generateAndSaveVizixEvent(VizixEventAction.START, VizixEventActivity.ASSOCIATION_CREATE, VIZIX_EVENT_ID_CREATE_ASSOCIATION, this.firstField, this.secondField);

    let isError = false; // To control the events in case of error
    let errorMessage = null; // To control the events in case of error

    this.vizixService.VizixPatchItem(body, {'upsert': 'true'}).subscribe(
      (data: any) => {
        console.log(this.tag, 'create product next', data);
        if (data && data[this.secondField] && data[this.secondField].ERROR) {
          this.onAlert({
            type: 'error',
            title: this.translate.instant('manage_tags.dialog_error_title'),
            message: this.translate.instant('manage_tags.dialog_error_message'),
            okButtonText: this.translate.instant('manage_tags.buttons.ok')
          });
        }
        else {
          if (this.isSoundAssociationReady) {
            this.nativeAudio.play(this.soundAssociation);
          }
          this.ngZone.run(() => {
            this.quantitySaved++;
          });
        }
      },
      (err) => {
        isError = true;
        errorMessage = err;

        this.dismissLoading();
        console.log(this.tag, 'create product error' + err);
        this.resetForm();
        this.onAlert({
          type: 'error',
          title: this.translate.instant('manage_tags.dialog_error_title'),
          message: this.translate.instant('manage_tags.dialog_error_message'),
          okButtonText: this.translate.instant('manage_tags.buttons.ok')
        });
      },
      () => {
        if (!isError) {
          this.generateAndSaveVizixEvent(VizixEventActivity.ASSOCIATION_CREATE, VIZIX_EVENT_ID_CREATE_ASSOCIATION, this.config.ThingTypeCodeToSync.value, this.firstField, this.secondField);
        } else {
          this.generateAndSaveVizixEvent(VizixEventActivity.ASSOCIATION_CREATE, VIZIX_EVENT_ID_CREATE_ASSOCIATION, this.config.ThingTypeCodeToSync.value, this.firstField, this.secondField, true, errorMessage);
        }
        this.dismissLoading();
        this.resetForm();
      });
  }

  /**
   * Update association
   * @constructor
   */
  private updateAssociation() {

    this.dismissLoading();

    this.onLoading({
      content: this.translate.instant('manage_tags.loading_update_association')
    })
      .then(() => {

        let body: PatchThingRequestItem[] = [];
        let requestItem = new PatchThingRequestItem();
        requestItem.timestamp = Date.now();
        requestItem.serialNumber = this.secondField;
        requestItem.group = this.vizixService.configurationApp.AppParametersConfiguration.Group.value;
        requestItem.thingTypeCode = this.config.ThingTypeCodeToSync.value;

      requestItem.udfs = {};
      requestItem.udfs[this.config.AssociationZoneUDF.value] = {value: String(this.vizixService.getStoreId()) + '_' + this.selectedZone.zoneTypeCode};
      requestItem.udfs[this.vizixService.configurationApp.AppParametersConfiguration.ItemEventUDF.value] = {value: ITEM_EVENT_RE_ASSOCIATION_TAG};
      // TODO: MOVE TO DATA ENTRY
      requestItem.udfs[ITEM_UDF_TS_REASSOCIATED] = {value: true};
      requestItem.udfs[ITEM_UDF_TS_REASSOCIATED_DATE] = {value: this.vizixService.vizixTime()};
      requestItem.udfs[ITEM_UDF_USER] = {value: this.username};
      requestItem.udfs[ITEM_UDF_UUID_MOBILE] = {value: this.uuid};
      requestItem.udfs[ITEM_UDF_SOURCE] = {value: VIZIX_THING_ITEM_UDF_SOURCE};
      requestItem.udfs[ITEM_UDF_CATEGORY] = {value: 'Workflow'};
      requestItem.udfs[ITEM_UDF_SUB_CATEGORY] = {value: 'Manage Tags'};
      requestItem.udfs[ITEM_UDF_ACTIVITY] = {value: 'Update Association'};
      requestItem.udfs[this.config.ItemUPCUDF.value] = {value: this.productSerialNumber};

      body.push(requestItem);
      // this.generateAndSaveVizixEvent(VizixEventAction.START, VizixEventActivity.ASSOCIATION_UPDATE, VIZIX_EVENT_ID_UPDATE_ASSOCIATION, this.firstField, this.secondField);

        let isError = false; // To control the events in case of error
        let errorMessage = null; // To control the events in case of error

        this.vizixService.VizixPatchItem(body).subscribe(
          (data: any) => {

            if (data && data[this.secondField] && data[this.secondField].ERROR) {
              this.onAlert({
                type: 'error',
                title: this.translate.instant('manage_tags.dialog_error_title'),
                message: this.translate.instant('manage_tags.dialog_error_message'),
                okButtonText: this.translate.instant('manage_tags.buttons.ok')
              });
            } else {
              if (this.isSoundAssociationReady) {
                this.nativeAudio.play(this.soundAssociation);
              }
              this.ngZone.run(() => {
                this.quantityUpdated++;
              });
            }
          },
          (err) => {
            isError = true;
            errorMessage = err;

            this.dismissLoading();
            console.log(this.tag, 'update product error' + err);
            this.onAlert({
              type: 'error',
              title: this.translate.instant('manage_tags.dialog_error_title'),
              message: this.translate.instant('manage_tags.dialog_error_message'),
              okButtonText: this.translate.instant('manage_tags.buttons.ok')
            });
            this.resetForm();
          },
          () => {
            if (!isError) {
              this.generateAndSaveVizixEvent(VizixEventActivity.ASSOCIATION_UPDATE, VIZIX_EVENT_ID_UPDATE_ASSOCIATION, this.config.ThingTypeCodeToSync.value, this.firstField, this.secondField);
            } else {
              this.generateAndSaveVizixEvent(VizixEventActivity.ASSOCIATION_UPDATE, VIZIX_EVENT_ID_UPDATE_ASSOCIATION, this.config.ThingTypeCodeToSync.value, this.firstField, this.secondField, true, errorMessage);
            }
            console.log(this.tag, 'update product complete');
            this.dismissLoading();
            this.resetForm();
          });

      });
  }

  /**
   * Insert default product
   */
  private insertDefaultProduct() {

    this.dismissLoading();

    this.onLoading({
      content: this.translate.instant('manage_tags.loading_create_default_product')
    })
      .then(() => {

        let body: PatchThingRequestItem[] = [];
        let requestItem = new PatchThingRequestItem();
        requestItem.timestamp = Date.now();
        requestItem.serialNumber = this.firstField;
        requestItem.name = this.firstField;
        requestItem.group = this.vizixService.configurationApp.AppParametersConfiguration.Group.value;
        requestItem.thingTypeCode = this.config.FirstThingTypeCode.value;
        requestItem.udfs = {};
        requestItem.udfs[this.config.DefaultProductUPCUDF.value] = {'value': this.firstField};
        requestItem.udfs[this.config.DefaultProductSKUUDF.value] = {'value': this.firstField};
        body.push(requestItem);

        let isError = false; // To control the events in case of error
        let errorMessage = null; // To control the events in case of error

        this.vizixService.VizixPatchItem(body, {'upsert': 'true'}).subscribe(
          (data: any) => {
            console.log(this.tag, 'Insert Defaul product next', data);
            if (data && data[this.firstField] && data[this.firstField].ERROR) {
              this.onAlert({
                type: 'error',
                title: this.translate.instant('manage_tags.dialog_error_title'),
                message: this.translate.instant('manage_tags.dialog_error_message'),
                okButtonText: this.translate.instant('manage_tags.buttons.ok')
              });
              this.resetForm();
            }
            else {
              this.verifySecondFieldExist();
            }
          },
          (err) => {
            isError = true;
            errorMessage = err;
            this.dismissLoading();
            console.log(this.tag, 'Insert Defaul product error' + err);
            this.resetForm();
            this.onAlert({
              type: 'error',
              title: this.translate.instant('manage_tags.dialog_error_title'),
              message: this.translate.instant('manage_tags.dialog_error_message'),
              okButtonText: this.translate.instant('manage_tags.buttons.ok')
            });
          },
          () => {
            if (!isError) {
              this.generateAndSaveVizixEvent(VizixEventActivity.CREATE_DEFAULT_PRODUCT, VIZIX_EVENT_ID_CREATE_DEFAULT_PRODUCT, this.config.FirstThingTypeCode.value, this.firstField);
            } else {
              this.generateAndSaveVizixEvent(VizixEventActivity.CREATE_DEFAULT_PRODUCT, VIZIX_EVENT_ID_CREATE_DEFAULT_PRODUCT, this.config.FirstThingTypeCode.value, this.firstField, null, true, errorMessage);
            }
            console.log(this.tag, 'Insert Default product complete');
            this.dismissLoading();
          });
      });
  }
}



// WEBPACK FOOTER //
// ./src/pages/manage-tags/manage-tags.ts